import { SiteDashboardWidgetData } from "~/api/Api";

/**
 * Static sample data used to render the Google Dashboard preview when a
 * site has access to the feature but hasn't connected Google Analytics or
 * created their first dashboard yet. The widget IDs and ordering must
 * match `DEFAULT_DASHBOARD.properties.widgets` exactly so `joinWidgets`
 * can pair them up.
 */
export const FAKE_WIDGETS_DATA: SiteDashboardWidgetData[] = [
  {
    id: "GA-total-organic-sessions-by-day",
    data: {
      dimension_headers: [{ name: "date" }],
      kind: "analyticsData#runReport",
      metadata: {
        currency_code: "USD",
        time_zone: "America/Chicago",
      },
      metric_headers: [
        { name: "sessions", type: "TYPE_INTEGER" },
        { name: "totalUsers", type: "TYPE_INTEGER" },
      ],
      row_count: 7,
      rows: [
        {
          dimension_values: [{ value: "20260404" }],
          metric_values: [{ value: "241" }, { value: "220" }],
        },
        {
          dimension_values: [{ value: "20260405" }],
          metric_values: [{ value: "231" }, { value: "209" }],
        },
        {
          dimension_values: [{ value: "20260406" }],
          metric_values: [{ value: "318" }, { value: "272" }],
        },
        {
          dimension_values: [{ value: "20260407" }],
          metric_values: [{ value: "557" }, { value: "513" }],
        },
        {
          dimension_values: [{ value: "20260408" }],
          metric_values: [{ value: "321" }, { value: "279" }],
        },
        {
          dimension_values: [{ value: "20260409" }],
          metric_values: [{ value: "449" }, { value: "411" }],
        },
        {
          dimension_values: [{ value: "20260410" }],
          metric_values: [{ value: "417" }, { value: "388" }],
        },
      ],
    },
  },
  {
    id: "GSC-essential-seo-metrics",
    data: {
      response_aggregation_type: "byProperty",
      rows: [
        {
          clicks: 228,
          ctr: 0.001553334559650091,
          impressions: 146781,
          position: 25.38592188362254,
        },
      ],
    },
  },
  {
    id: "GSC-top-landing-pages",
    data: {
      response_aggregation_type: "byPage",
      rows: [
        {
          clicks: 141,
          ctr: 0.023414148123546995,
          impressions: 6022,
          keys: ["https://www.example.com/"],
          position: 16.458485552972434,
        },
        {
          clicks: 9,
          ctr: 0.0008606674954575882,
          impressions: 10457,
          keys: ["https://www.example.com/seo-glossary/404-error"],
          position: 6.012527493544994,
        },
        {
          clicks: 4,
          ctr: 0.0061068702290076335,
          impressions: 655,
          keys: ["https://www.example.com/features/ai-schema-markup-generator"],
          position: 21.094656488549617,
        },
        {
          clicks: 4,
          ctr: 0.0050568900126422255,
          impressions: 791,
          keys: ["https://www.example.com/pricing"],
          position: 5.0505689001264225,
        },
        {
          clicks: 3,
          ctr: 0.006944444444444444,
          impressions: 432,
          keys: ["https://www.example.com/ai-saas-seo-automation"],
          position: 13.506944444444445,
        },
        {
          clicks: 2,
          ctr: 0.021739130434782608,
          impressions: 92,
          keys: ["https://www.example.com/about"],
          position: 5.706521739130435,
        },
        {
          clicks: 2,
          ctr: 0.017094017094017096,
          impressions: 117,
          keys: ["https://www.example.com/ai-wix-seo-automation"],
          position: 9.051282051282051,
        },
        {
          clicks: 2,
          ctr: 0.004149377593360996,
          impressions: 482,
          keys: ["https://www.example.com/competitors/otto-seo-review"],
          position: 6.33402489626556,
        },
        {
          clicks: 2,
          ctr: 0.0017985611510791368,
          impressions: 1112,
          keys: ["https://www.example.com/competitors/search-atlas-review"],
          position: 13.182553956834532,
        },
        {
          clicks: 2,
          ctr: 0.0005417118093174431,
          impressions: 3692,
          keys: ["https://www.example.com/features/ai-seo-automation-software"],
          position: 16.92578548212351,
        },
      ],
    },
  },
  {
    id: "GSC-top-search-queries",
    data: {
      response_aggregation_type: "byProperty",
      rows: [
        {
          clicks: 106,
          ctr: 0.37857142857142856,
          impressions: 280,
          keys: ["seo tools"],
          position: 1.5178571428571428,
        },
        {
          clicks: 9,
          ctr: 0.21951219512195122,
          impressions: 41,
          keys: ["seo software"],
          position: 6.390243902439025,
        },
        {
          clicks: 7,
          ctr: 0.0010215995329830706,
          impressions: 6852,
          keys: ["404 error"],
          position: 5.305604203152364,
        },
        {
          clicks: 4,
          ctr: 0.03571428571428571,
          impressions: 112,
          keys: ["seo platform"],
          position: 2.6517857142857144,
        },
        {
          clicks: 2,
          ctr: 0.13333333333333333,
          impressions: 15,
          keys: ["seo audit tool"],
          position: 1,
        },
        {
          clicks: 2,
          ctr: 0.0007122507122507123,
          impressions: 2808,
          keys: ["error 404"],
          position: 3.8208689458689458,
        },
        {
          clicks: 1,
          ctr: 0.0016638935108153079,
          impressions: 601,
          keys: ["ai seo"],
          position: 37.59900166389351,
        },
        {
          clicks: 1,
          ctr: 0.007874015748031496,
          impressions: 127,
          keys: ["ai seo agent"],
          position: 23.31496062992126,
        },
        {
          clicks: 1,
          ctr: 0.5,
          impressions: 2,
          keys: ["ai seo saas"],
          position: 1,
        },
        {
          clicks: 1,
          ctr: 0.0030120481927710845,
          impressions: 332,
          keys: ["seo agency"],
          position: 1.4156626506024097,
        },
      ],
    },
  },
  {
    id: "GA-content-engagement-trends",
    data: {
      dimension_headers: [{ name: "date" }],
      kind: "analyticsData#runReport",
      metadata: {
        currency_code: "USD",
        time_zone: "America/Chicago",
      },
      metric_headers: [
        { name: "screenPageViews", type: "TYPE_INTEGER" },
        { name: "totalUsers", type: "TYPE_INTEGER" },
      ],
      row_count: 7,
      rows: [
        {
          dimension_values: [{ value: "20260404" }],
          metric_values: [{ value: "376" }, { value: "220" }],
        },
        {
          dimension_values: [{ value: "20260405" }],
          metric_values: [{ value: "288" }, { value: "209" }],
        },
        {
          dimension_values: [{ value: "20260406" }],
          metric_values: [{ value: "522" }, { value: "272" }],
        },
        {
          dimension_values: [{ value: "20260407" }],
          metric_values: [{ value: "691" }, { value: "513" }],
        },
        {
          dimension_values: [{ value: "20260408" }],
          metric_values: [{ value: "465" }, { value: "279" }],
        },
        {
          dimension_values: [{ value: "20260409" }],
          metric_values: [{ value: "636" }, { value: "411" }],
        },
        {
          dimension_values: [{ value: "20260410" }],
          metric_values: [{ value: "584" }, { value: "388" }],
        },
      ],
    },
  },
  {
    id: "GA-total-organic-sessions",
    data: {
      kind: "analyticsData#runReport",
      metadata: {
        currency_code: "USD",
        time_zone: "America/Chicago",
      },
      metric_headers: [
        { name: "sessions", type: "TYPE_INTEGER" },
        { name: "totalUsers", type: "TYPE_INTEGER" },
      ],
      row_count: 1,
      rows: [
        {
          metric_values: [{ value: "2535" }, { value: "2181" }],
        },
      ],
    },
  },
  {
    id: "GA-content-engagement-trends-rate",
    data: {
      dimension_headers: [{ name: "date" }],
      kind: "analyticsData#runReport",
      metadata: {
        currency_code: "USD",
        time_zone: "America/Chicago",
      },
      metric_headers: [{ name: "engagementRate", type: "TYPE_FLOAT" }],
      row_count: 7,
      rows: [
        {
          dimension_values: [{ value: "20260404" }],
          metric_values: [{ value: "0.2863070539419087" }],
        },
        {
          dimension_values: [{ value: "20260405" }],
          metric_values: [{ value: "0.30303030303030304" }],
        },
        {
          dimension_values: [{ value: "20260406" }],
          metric_values: [{ value: "0.38050314465408808" }],
        },
        {
          dimension_values: [{ value: "20260407" }],
          metric_values: [{ value: "0.25852782764811488" }],
        },
        {
          dimension_values: [{ value: "20260408" }],
          metric_values: [{ value: "0.42990654205607476" }],
        },
        {
          dimension_values: [{ value: "20260409" }],
          metric_values: [{ value: "0.34075723830734966" }],
        },
        {
          dimension_values: [{ value: "20260410" }],
          metric_values: [{ value: "0.39124668435013263" }],
        },
      ],
    },
  },
];
